"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type OutputFormat = "lines" | "comma";

const EMAIL_REGEX = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const TRAILING_DOTS_REGEX = /\.+$/;

function extractEmails(text: string, sorted: boolean): string[] {
  const matches = text.match(EMAIL_REGEX) ?? [];
  const seen = new Set<string>();
  const emails: string[] = [];
  for (const match of matches) {
    const email = match.replace(TRAILING_DOTS_REGEX, "");
    const key = email.toLowerCase();
    if (email.length > 0 && !seen.has(key)) {
      seen.add(key);
      emails.push(email);
    }
  }
  if (sorted) {
    emails.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }
  return emails;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const EXTRACT_EMAILS_STRINGS = {
  onePerLine: "One per line",
  commaSeparated: "Comma-separated",
  options: "Options",
  sortAZ: "Sort A-Z",
  textToScan: "Text to scan",
  inputPlaceholder:
    "Contact jane.doe@example.com or sales@example.org for details.",
  extractedEmails: "Extracted emails",
  emailsFound: "Emails found",
  outputFormat: "Output format",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
} as const;

export default function ExtractEmailsWidget() {
  const [input, setInput] = useState("");
  const [sorted, setSorted] = useState(false);
  const [format, setFormat] = useState<OutputFormat>("lines");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(EXTRACT_EMAILS_STRINGS);

  const fmt = useToolNumber();

  const outputFormats: { value: OutputFormat; label: string }[] = [
    { value: "lines", label: strings.onePerLine },
    { value: "comma", label: strings.commaSeparated },
  ];

  const emails = extractEmails(input, sorted);
  const output = format === "lines" ? emails.join("\n") : emails.join(", ");

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <div className="flex flex-wrap gap-3">
          {outputFormats.map((f) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={f.value}
            >
              <input
                checked={format === f.value}
                className="accent-primary"
                name="output-format"
                onChange={() => setFormat(f.value)}
                type="radio"
                value={f.value}
              />
              {f.label}
            </label>
          ))}
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={sorted}
              className="accent-primary"
              onChange={(e) => setSorted(e.target.checked)}
              type="checkbox"
            />
            {strings.sortAZ}
          </label>
        </div>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="emails-input"
        >
          {strings.textToScan}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="emails-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="emails-output"
          >
            {strings.extractedEmails}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="emails-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Stats */}
      {input.trim().length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-2">
          <StatCard label={strings.emailsFound} value={fmt(emails.length)} />
          <StatCard
            label={strings.outputFormat}
            value={
              format === "lines" ? strings.onePerLine : strings.commaSeparated
            }
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
    </>
  );
}
